import { memo } from "react";

import { ResponsiveLine, Serie } from "@nivo/line";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import { generateTicks } from "utility/charting_utils";

// -----------------------------------------------------------------------------

const LineChart = memo(function LineChart({
  title,
  data = [],
  loading = false,
  isOpen,
}: LineChartProps) {
  const hasAtLeastOneDatapoint = data.length > 0;

  //
  return (
    <Card
      sx={{
        width: "100%",
        minWidth: "400px",
        position: "relative",
        height: "125px",
        overflow: "visible",
      }}
    >
      <CardContent sx={{ padding: "8px 16px" }}>
        <Typography variant="overline">{title}</Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {loading ? (
              <>
                &nbsp;
                <CircularProgress size={20} />
              </>
            ) : !hasAtLeastOneDatapoint ? (
              "None"
            ) : null}
          </Typography>
        </Box>
      </CardContent>
      {hasAtLeastOneDatapoint ? (
        <Box sx={{ height: 75 }}>
          <ResponsiveLine
            data={data}
            enablePoints={false}
            areaBlendMode="multiply"
            areaOpacity={0.9}
            colors={isOpen ? ["#FF6257"] : ["#3C9D40"]}
            margin={{ top: 5, right: 40, bottom: 35, left: 40 }}
            yScale={{
              type: "linear",
              min: "auto",
              max: "auto",
            }}
            enableGridX={false}
            enableGridY={false}
            axisBottom={{
              tickValues: generateTicks(data[0].data),
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
            }}
            axisLeft={{
              tickValues: 2,
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
            }}
            pointSize={10}
            pointColor={{ theme: "background" }}
            pointBorderWidth={2}
            pointBorderColor={{ from: "serieColor" }}
            pointLabelYOffset={-12}
            defs={[
              {
                id: "gradientOpen",
                type: "linearGradient",
                colors: [
                  { offset: 0, color: "hsla(4, 90%, 47%, 0.1)" },
                  { offset: 100, color: "hsla(4, 90%, 47%, 0)" },
                ],
              },
              {
                id: "gradientResolved",
                type: "linearGradient",
                colors: [
                  { offset: 0, color: "hsla(122, 45%, 43%, 0.1)" },
                  { offset: 100, color: "hsla(0, 0%, 100%, 0)" },
                ],
              },
            ]}
            fill={[
              { match: "*", id: isOpen ? "gradientOpen" : "gradientResolved" },
            ]}
          />
        </Box>
      ) : null}
    </Card>
  );
});

export default LineChart;

// -----------------------------------------------------------------------------

interface LineChartProps {
  title: JSX.Element | string;
  data: Serie[];
  loading?: boolean;
  isOpen: boolean;
}
